var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.platform.compare",
  title: "Universe vs the stack — Compare",
  description: "The honest comparison: one integrated spine you own vs a duct-taped stack of separate tools. Positioning-first; the table merely confirms.",
  supportServer: true,
  fields: { reserveUrl: { title: "Reserve URL", type: "uri", description: "Reservation entry point.", required: false } }
});
var S = require("../helpers/egt.sections.js");

/* The honest side-by-side. Built from the marketing spec (vs-the-stack):
 * positioning-first — the reader has already decided emotionally before the
 * table, which only CONFIRMS. Premium, never cheaper: this is a value +
 * ownership story, never "cheaper than the stack". We compare against the
 * PATTERN ("the stack"), never a named brand; the VOC "voice echoes" are our
 * own paraphrase of owner sentiment, NOT sourced testimonials. No published
 * prices, no metrics, no dates — EGT posture (vision + hype, present tense). */

/* ---- the four hidden costs of the stack (VOC-led) ------------------- */
var COSTS = [
  { h: "Subscription creep",
    p: "Each app looks like $20–$40. Then you do the bookkeeping and the “stack” is your second-biggest line item.",
    v: "It feels like profit equals subscriptions." },
  { h: "Tools that don't talk",
    p: "Your store doesn't know your email list. Your email tool doesn't know who bought. Your CRM is whatever you remembered to type in twice.",
    v: "Nothing's synced. I'm the integration." },
  { h: "The glue you maintain",
    p: "Webhooks, CSV exports, an “automation” nobody fully understands. That's the integration tax — and you pay it in evenings.",
    v: "The real cost isn't the fees. It's the glue." },
  { h: "It breaks when you touch it",
    p: "A plugin update, an API change, a payment that silently fails, an email that quietly lands in spam. Five vendors — and the one who's accountable is you.",
    v: "It just works… until one day it doesn't." }
];

/* ---- the side-by-side decision axes (Universe present-tense) -------- */
var AXES = [
  { k: "Logins & dashboards",
    u: "One login. One place to run the whole business.",
    s: "Five-plus logins, five-plus dashboards, five-plus password resets." },
  { k: "One customer truth",
    u: "Purchase, email, membership, support — one customer record that updates itself from real activity.",
    s: "Who bought lives in the store; who you emailed lives in the email tool; the “CRM” is whatever got typed in twice." },
  { k: "Integration",
    u: "A shared data model — the pillars know about each other natively. No glue to build or babysit.",
    s: "Zapier, webhooks, CSV exports, and sync that breaks quietly. You are the integration." },
  { k: "Email that lands",
    u: "Built in, on our own sending infrastructure — real deliverability, our own relay.",
    s: "A separate sender, separate reputation setup, separate “why is this in spam.”" },
  { k: "Checkout & payments",
    u: "One universal checkout with saved cards, powered by Stripe — its processing fees a separate, honest line.",
    s: "A builder's checkout plus a payments add-on plus extra apps for the parts that should've been included." },
  { k: "Memberships & subscriptions",
    u: "Memberships live inside your own site and store, on your own customer record.",
    s: "A separate creator app that takes its own cut and owns the relationship." },
  { k: "Your brand",
    u: "A luxury-grade experience that stays unmistakably yours — never a flattened template.",
    s: "As many brand systems as you have tools; coherence is your job to enforce." },
  { k: "Who handles the hard stuff",
    u: "We do — deliverability, login security, payments, caching, speed and SEO are baked in and invisible.",
    s: "You're the systems integrator, the on-call engineer, and the one vendor everyone points at." },
  { k: "An AI partner",
    u: "Intrepid prepares campaigns, replies, and pages with you — it proposes, never acts; it never charges, sends, or publishes without your say-so, and never writes your login or payments.",
    s: "Bolt-on AI per tool, no shared context — and the 2026 horror story of an AI that shipped something that looked finished, then leaked thousands of people's data through a broken login." },
  { k: "Leaving / your data",
    u: "Export your customers, orders, content, and email list anytime — held as a grant, not a copy.",
    s: "Data scattered across vendors; migrating means stitching exports from each and praying nothing breaks." }
];

/* ---- when best-of-breed still wins (the credibility keystone) ------- */
var HONEST = [
  { h: "A function becomes mission-critical and specialized.",
    p: "High-volume ad operations, advanced analytics, a niche workflow nobody builds for — a dedicated specialist may outperform any integrated pillar. That's real." },
  { h: "You already run a deep setup that works.",
    p: "Years invested in a specialized stack your team knows cold? “Rip and replace” isn't automatically smart. Move the core first; keep the specialist where it earns its keep." },
  { h: "A specialist tool is genuinely deeper for your edge case.",
    p: "A mature, single-purpose product has had years to go deep on one job. Because your data stays yours and exportable, plugging it alongside Universe isn't a trap." },
  { h: "You want maximum composability — and you have the chops.",
    p: "Some technical owners genuinely prefer wiring their own stack. Universe is the reliable, integrated core you don't have to babysit — and it has open edges when you want them." }
];

/* ---- the migration path (safe, staged, reversible) ----------------- */
var STEPS = [
  { n: "01", h: "Bring your site & domain.",
    p: "Point your domain, keep your existing URLs, and stay fast and crawlable from day one — so your rankings come with you instead of resetting." },
  { n: "02", h: "Unify the core.",
    p: "Move store, checkout, email, and customers onto one spine. One login, one customer truth — the glue and the duplicate data simply disappear." },
  { n: "03", h: "Fold in the rest on your timeline.",
    p: "Move memberships, marketing, and loyalty onto the same spine whenever you're ready. And because your data is always exportable, nothing here is a one-way door." }
];

function costCard(c, i) {
  return { n: "article", c: "egt-cmp-cost egt-reveal", style: "--egt-i:" + i,
    i: [
      { n: "h3", c: "egt-cmp-cost__h", i: c.h },
      { n: "p", c: "egt-cmp-cost__p", i: c.p },
      { n: "p", c: "egt-cmp-cost__v", i: [ "“", c.v, "”" ] }
    ] };
}

/* The side-by-side is a REAL semantic <table> (SEO wave 2C-8, 2026-10-09):
 * <tr>/<th scope>/<td> instead of ARIA-roled divs, so crawlers and answer
 * engines read it as a comparison table. Same classes, same grid layout —
 * egt.compare.scss resets the table display so nothing moves visually. */
function axisRow(a, i) {
  return { n: "tr", c: "egt-cmp-row egt-reveal", style: "--egt-i:" + i,
    i: [
      { n: "th", c: "egt-cmp-row__axis", scope: "row", i: a.k },
      { n: "td", c: "egt-cmp-row__u",
        i: [ { n: "span", c: "egt-cmp-row__tag egt-cmp-row__tag--u", i: "Universe" }, { n: "p", i: a.u } ] },
      { n: "td", c: "egt-cmp-row__s",
        i: [ { n: "span", c: "egt-cmp-row__tag", i: "The stack" }, { n: "p", i: a.s } ] }
    ] };
}

exports.render = async function render(options) {
  var reserve = (options && options.reserveUrl) || "/reserve";
  return S.page(reserve, [

    /* 1 — HERO: win the frame in two seconds (the tangle -> one) */
    { n: "section", c: "egt-section egt-canvas egt-canvas--center egt-canvas--tall egt-cmp-hero",
      i: [
        S.media({ img: "/imgs/egt-platform-canvas.jpg", alt: "", parallax: 0.06, priority: true }),
        { n: "div", c: "egt__shell egt-canvas__inner",
          i: S.glass({ width: "wide", c: "egt-cmp-hero__card egt-reveal", content: [
            { n: "p", c: "egt__eyebrow", i: "Compare · one spine vs the stack" },
            { n: "h1", c: "egt-cmp-hero__title", i: [ "One platform, or ", { n: "span", c: "egt__gold", i: "ten tabs?" } ] },
            { n: "p", c: "egt-cmp-hero__lead",
              i: "A website builder, a few commerce apps, an email tool, a payment add-on, a CRM, a membership app — that's five logins, five dashboards, and nothing that talks to anything else. The Universe Platform is one real integrated spine for your whole business: site, store, checkout, payments, email, and customers, already wired together — on a brand and a customer record you own." },
            { n: "div", c: "egt-cmp-hero__actions", i: S.cta(reserve, "Join Beta", true) },
            { n: "p", c: "egt-cmp-hero__note", i: "An honest comparison — we'll even tell you when a separate tool still makes sense." }
          ] }) }
      ] },

    /* 2 — the real cost of the stack (VOC) */
    S.section({
      eyebrow: "The real cost of the stack",
      title: "The stack has a bill you never signed up for.",
      lead: "It's not just the subscriptions. It's the glue, the context-switching, and the 11pm “why did this break.”",
      body: [ { n: "div", c: "egt-cmp-costs egt-reveal", i: COSTS.map(costCard) } ]
    }),

    /* 3 — the honest objection (defuse directly) */
    { n: "section", c: "egt-section egt-section--center egt-cmp-objection",
      i: { n: "div", c: "egt__shell",
        i: [
          { n: "blockquote", c: "egt-cmp-objection__q egt-reveal",
            i: "“Isn't all-in-one just jack of all trades, master of none?”" },
          { n: "p", c: "egt-cmp-objection__open egt-reveal",
            i: "Fair. Most “all-in-one” suites earned that reputation — a pile of half-built modules glued under one login. So here's our honest answer." },
          { n: "ol", c: "egt-cmp-beats egt-reveal",
            i: [
              { n: "li", c: "egt-cmp-beat", i: [ { n: "h3", i: "One spine, not a bundle of apps." },
                { n: "p", i: "Universe isn't ten products wearing one logo. The site, the store, checkout, email, and your customer record share a single data model — so they actually know about each other. That's the difference between integrated and merely “all in one place.”" } ] },
              { n: "li", c: "egt-cmp-beat", i: [ { n: "h3", i: "Each pillar is built to a craft bar, not a checkbox." },
                { n: "p", i: "We'd rather do a few things genuinely well than feature-match everyone shallowly. Site, store, checkout, email, and your customer record are all here — each built to a craft bar. No pretending a stub is a product." } ] },
              { n: "li", c: "egt-cmp-beat", i: [ { n: "h3", i: "No upsell maze — it won't force you into its workflow." },
                { n: "p", i: "Your business is described in your own words — your products, your prices, your voice — not bent into a generic operating model. You see exactly what's included." } ] },
              { n: "li", c: "egt-cmp-beat", i: [ { n: "h3", i: "And if we're ever not enough — you can leave." },
                { n: "p", i: "Your customers, orders, content, and email list are yours, held as a grant, not a copy. Export them and walk anytime. An integrated core shouldn't mean a locked door — and ours doesn't." } ] }
            ] },
          { n: "p", c: "egt-cmp-objection__close egt-reveal",
            i: [ "Not another Franken-stack. Not another bloated upsell suite. ", { n: "span", c: "egt__gold", i: "One spine you own — one source of truth." } ] }
        ] } },

    /* 4 — the side-by-side (confirms, doesn't argue) */
    S.section({
      eyebrow: "The side-by-side",
      title: "The same business, two ways to run it.",
      lead: "Everything in the Universe column is part of the platform — one real integrated spine you own, not a pile of apps you wire together. This is about integration and ownership, not a cheaper bill.",
      c: "egt-cmp-table-section",
      body: [
        { n: "table", c: "egt-cmp-table", "aria-label": "Universe versus the stack",
          i: [
            { n: "thead", c: "egt-cmp-table__head",
              i: { n: "tr", c: "egt-cmp-row egt-cmp-row--head",
                i: [
                  { n: "td", c: "egt-cmp-row__axis", i: "" },
                  { n: "th", c: "egt-cmp-row__u", scope: "col", i: "Universe — one spine" },
                  { n: "th", c: "egt-cmp-row__s", scope: "col", i: "The stack — glued best-of-breed" }
                ] } },
            { n: "tbody", c: "egt-cmp-table__body", i: AXES.map(axisRow) }
          ] },
        { n: "p", c: "egt-cmp-foot",
          i: "Everything in the Universe column is part of the platform. Universe is a premium, fully managed platform — consolidating a stack onto one spine is a value and ownership story, not a claim that Universe costs less. Stripe's processing fees are a separate, honest line through your own account." }
      ]
    }),

    /* 5 — when best-of-breed still makes sense (the credibility keystone) */
    S.section({
      eyebrow: "When a separate tool still makes sense",
      title: "We're not going to tell you one platform is always the answer.",
      lead: "Sometimes it isn't — here's when, honestly.",
      body: [
        { n: "div", c: "egt-cmp-honest egt-reveal",
          i: HONEST.map(function (h, i) {
            return { n: "article", c: "egt-cmp-honest__item egt-reveal", style: "--egt-i:" + i,
              i: [ { n: "h3", i: h.h }, { n: "p", i: h.p } ] };
          }) },
        { n: "p", c: "egt-cmp-bridge egt-reveal",
          i: "Glue your basics together and you'll bleed time and money on plumbing forever. Unify the core that should never be glued — site, store, checkout, email, customers — and plug in a specialist only where it truly earns it. That's the smart stack." }
      ]
    }),

    /* 6 — collapse your stack (migration path) */
    S.section({
      eyebrow: "Collapse your stack",
      title: "One piece at a time.",
      lead: "You don't have to move everything in a weekend. Start with the core, keep your URLs, and bring the rest when you're ready.",
      body: [
        { n: "ol", c: "egt-cmp-steps egt-reveal",
          i: STEPS.map(function (s, i) {
            return { n: "li", c: "egt-cmp-step egt-reveal", style: "--egt-i:" + i,
              i: [ { n: "span", c: "egt-cmp-step__n", i: s.n },
                { n: "h3", c: "egt-cmp-step__h", i: s.h },
                { n: "p", c: "egt-cmp-step__p", i: s.p } ] };
          }) },
        { n: "p", c: "egt-cmp-reassure egt-reveal",
          i: "Your data stays yours, held as a grant, not a copy · Keep your URLs, stay crawlable · Bring it over in stages, not all at once." }
      ]
    }),

    /* 7 — reserve crescendo: full-viewport close, matching the /platform +
     * capability-page .egt-pf-reserve pattern (WP-N6.7), in compare's own voice */
    { n: "section", c: "egt-section egt-canvas egt-canvas--center egt-canvas--tall egt-pf-reserve",
      i: [
        S.media({ img: "/imgs/egt-platform-canvas.jpg", alt: "", parallax: 0.05 }),
        { n: "div", c: "egt__shell egt-canvas__inner",
          i: S.glass({ width: "wide", c: "egt-pf-reserve__card egt-reveal", content: [
            { n: "p", c: "egt__eyebrow", i: "One platform. Your brand. Your whole business." },
            { n: "h2", c: "egt-pf-reserve__title", i: [ "Stop renting a stack that ", { n: "span", c: "egt__gold", i: "doesn't talk to itself." } ] },
            { n: "p", c: "egt-pf-reserve__lead", i: "Join the public Beta for $9.57 — a non-refundable reservation, credited in full toward your launch, and provisioned first. Collapse the stack onto one spine you own, and bring the rest when you're ready." },
            { n: "ul", c: "egt-pf-reserve__points", "aria-label": "What your reservation holds",
              i: [
                { n: "li", c: "egt-pf-reserve__point", i: [ { n: "span", c: "egt-pf-reserve__point-k", i: "Held for you" }, "Your founding place, kept the moment you reserve." ] },
                { n: "li", c: "egt-pf-reserve__point", i: [ { n: "span", c: "egt-pf-reserve__point-k", i: "Non-refundable, credited" }, "The full $9.57 applies to your launch — nothing lost." ] },
                { n: "li", c: "egt-pf-reserve__point", i: [ { n: "span", c: "egt-pf-reserve__point-k", i: "First in line" }, "Provisioned ahead of everyone else." ] }
              ] },
            { n: "div", c: "egt-phero__actions egt-pf-reserve__actions", i: S.cta(reserve, "Reserve your spot", true) },
            { n: "p", c: "egt-pf-reserve__fine", i: "Your founding place, held for you." }
          ] }) }
      ] },
    /* JSON-LD: WebPage + BreadcrumbList about the /platform app (SEO wave 2B-7) */
    S.pageSchema({ path: "/platform/compare/the-stack", name: "One platform or ten tabs? Universe vs the stack",
      about: "/platform#app",
      crumbs: [ { name: "Platform", path: "/platform" }, { name: "Universe vs the stack" } ] })
  ], { betaCta: true, footerCaptureLabel: S.FOOTER_LABELS.platform });
};
